import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronDown, ChevronUp, CheckSquare, Square, Play, Sparkles } from 'lucide-react';
import Button from '../../../components/ui/Button';

export default function Phase0VersesCard({ verses = [] }) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const phase0Verses = useMemo(
    () => verses?.filter(v => (v?.currentPhase ?? 0) === 0),
    [verses]
  );

  if (phase0Verses?.length === 0) return null;

  const allSelected = selectedIds?.length === phase0Verses?.length && phase0Verses?.length > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(phase0Verses?.map(v => v?.id));
    }
  };

  const toggleVerse = (id) => {
    setSelectedIds(prev =>
      prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleStartMemorizing = () => {
    const toMemorize = phase0Verses?.filter(v => selectedIds?.includes(v?.id));
    if (toMemorize?.length === 0) return;
    navigate('/memorization-practice', {
      state: {
        phase0Verses: toMemorize,
        isPhase0Session: true,
      }
    });
  };

  return (
    <div className="mt-4 rounded-xl overflow-hidden shadow-md border border-primary/20">
      {/* Gradient Header */}
      <button
        onClick={() => setIsExpanded(prev => !prev)}
        className="w-full flex items-center justify-between px-5 py-4 transition-all duration-200 bg-primary/10 border-b border-primary/20"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/20 flex-shrink-0">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="text-base font-bold text-foreground leading-tight">
              Ready to Start Memorizing
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {phase0Verses?.length} verse{phase0Verses?.length !== 1 ? 's' : ''} waiting to begin
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          {selectedIds?.length > 0 && (
            <span className="text-xs font-semibold text-primary-foreground bg-primary px-2.5 py-1 rounded-full shadow-sm">
              {selectedIds?.length} selected
            </span>
          )}
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/15">
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-primary" />
            ) : (
              <ChevronDown className="h-4 w-4 text-primary" />
            )}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="bg-card">
          {/* Select All + Action Row */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-primary/5">
            <button
              onClick={toggleAll}
              className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {allSelected ? (
                <CheckSquare className="h-4 w-4" />
              ) : (
                <Square className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-muted-foreground hover:text-foreground transition-colors">
                {allSelected ? 'Deselect All' : 'Select All'}
              </span>
            </button>

            <Button
              variant="default"
              size="sm"
              onClick={handleStartMemorizing}
              disabled={selectedIds?.length === 0}
              className="text-xs h-8 gap-1.5 shadow-sm"
            >
              <Play className="h-3.5 w-3.5" />
              Start Memorizing
              {selectedIds?.length > 0 && (
                <span className="ml-1 bg-white/20 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {selectedIds?.length}
                </span>
              )}
            </Button>
          </div>

          {/* Verse List */}
          <div className="divide-y divide-border/60 max-h-72 overflow-y-auto">
            {phase0Verses?.map(verse => {
              const isSelected = selectedIds?.includes(verse?.id);
              return (
                <button
                  key={verse?.id}
                  onClick={() => toggleVerse(verse?.id)}
                  className={`w-full flex items-start gap-3 px-5 py-3.5 text-left transition-all duration-150 ${
                    isSelected
                      ? 'bg-primary/8 border-l-2 border-primary' :'hover:bg-muted/30 border-l-2 border-transparent'
                  }`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {isSelected ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : (
                      <Square className="h-4 w-4 text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                        {verse?.reference}
                      </span>
                      {verse?.bible_version && (
                        <span className="text-xs text-muted-foreground uppercase bg-muted/60 px-1.5 py-0.5 rounded">
                          {verse?.bible_version}
                        </span>
                      )}
                    </div>
                    {verse?.text && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                        {verse?.text}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              Select verses, then tap <strong className="text-foreground">Start Memorizing</strong> to choose a practice mode.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
